import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import locations from "../../data/Locations";
import SearchableDropdown from "./SearchableDropdown";

const VEHICLE_TYPES = [
  "VIP Bus",
  "STC Bus",
  "Metro Mass Transit",
  "Private Bus",
  "Mini Bus",
  "Other",
];

export default function IntercityTravelForm({
  onSubmit,
  onBack,
  initialData = {},
}) {
  // Check if coverage was already selected from PackageDrawer
  const preSelectedCoverage = initialData.coverageDuration || null;
  const isPreSelected = !!preSelectedCoverage;

  // Extract coverage durations from policy data
  const coverageDurations = useMemo(() => {
    return initialData.originalPolicy?.coverage_durations || [];
  }, [initialData.originalPolicy?.coverage_durations]);

  // Get unique trip types from coverage durations
  const availableTripTypes = useMemo(() => {
    const types = [...new Set(coverageDurations.map((cd) => cd.route_type))];
    return types.filter(Boolean);
  }, [coverageDurations]);

  const [tripType, setTripType] = useState(
    initialData.tripType || preSelectedCoverage?.route_type || ""
  );
  const [selectedCoverage, setSelectedCoverage] = useState(() => {
    if (initialData.selectedCoverage) return initialData.selectedCoverage;
    if (preSelectedCoverage) {
      return {
        region: preSelectedCoverage.route_destination,
        price: preSelectedCoverage.price,
        hours: preSelectedCoverage.hours || preSelectedCoverage.display_value,
        unit: preSelectedCoverage.display_unit || "hours",
        coverage: preSelectedCoverage,
      };
    }
    return null;
  });
  const [busLicensePlate, setBusLicensePlate] = useState(
    initialData.busLicensePlate || ""
  );
  const [vehicleType, setVehicleType] = useState(
    initialData.vehicleType || "VIP Bus"
  );
  const [customVehicleType, setCustomVehicleType] = useState(
    initialData.customVehicleType || ""
  );
  const depRegion = "Greater Accra";
  const [depTown, setDepTown] = useState(initialData.departureCity || "Accra");
  const [destRegion, setDestRegion] = useState(
    initialData.destinationRegion ||
      preSelectedCoverage?.route_destination ||
      ""
  );
  const [destTown, setDestTown] = useState(initialData.destinationCity || "");

  // Helper function to strip " Region" suffix for location lookup
  const normalizeRegionName = (regionName) => {
    if (!regionName) return "";
    return regionName.replace(/ Region$/i, "").trim();
  };

  const availableDestinations = useMemo(() => {
    if (!tripType) return [];
    return coverageDurations
      .filter((cd) => cd.route_type === tripType)
      .map((cd) => ({
        region: cd.route_destination,
        price: cd.price,
        hours: cd.hours || cd.display_value,
        unit: cd.display_unit || "hours",
        coverage: cd,
      }));
  }, [tripType, coverageDurations]);

  // Auto-set destination region when coverage is selected
  useEffect(() => {
    if (selectedCoverage) {
      setDestRegion(selectedCoverage.region);
    }
  }, [selectedCoverage]);

  const departureCities = useMemo(() => {
    const normalizedRegion = normalizeRegionName(depRegion);
    return normalizedRegion ? locations[normalizedRegion] || [] : [];
  }, [depRegion]);

  const destinationCities = useMemo(() => {
    const normalizedRegion = normalizeRegionName(destRegion);
    return normalizedRegion ? locations[normalizedRegion] || [] : [];
  }, [destRegion]);

  const plateValid = useMemo(
    () => /[A-Za-z0-9-]{5,}/.test(busLicensePlate),
    [busLicensePlate]
  );
  const vehicleTypeValid =
    vehicleType && (vehicleType !== "Other" || customVehicleType.trim());
  const isValid =
    tripType &&
    selectedCoverage &&
    plateValid &&
    vehicleTypeValid &&
    depRegion &&
    depTown &&
    destRegion &&
    destTown;

  const handleSubmit = () => {
    const data = {
      tripType,
      routeDestination: selectedCoverage.region,
      coverageDuration: selectedCoverage.coverage,
      busLicensePlate: busLicensePlate.trim(),
      vehicleType: vehicleType === "Other" ? customVehicleType : vehicleType,
      departureRegion: depRegion,
      departureCity: depTown,
      destinationRegion: destRegion,
      destinationCity: destTown,
    };
    onSubmit(data);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="mb-6 text-center">
        <h3 className="mb-2 text-xl font-bold text-gray-900">Travel Details</h3>
        <p className="text-sm text-gray-600">
          Please provide your intercity travel information
        </p>
      </div>

      {/* Show selected coverage if pre-selected from PackageDrawer */}
      {isPreSelected && selectedCoverage && (
        <div className="p-4 mb-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <div className="flex gap-3 items-start">
            <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-emerald-100 rounded-lg">
              <svg
                className="w-5 h-5 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm font-semibold text-emerald-900">
                Selected Coverage
              </p>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-gray-900">
                    {selectedCoverage.region}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {tripType === "regional" ? "Regional" : "International"} •{" "}
                    {selectedCoverage.hours} {selectedCoverage.unit} coverage
                  </p>
                </div>
                <p className="text-lg font-bold text-emerald-600">
                  ₵{selectedCoverage.price}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trip Type Selection - Only show if not pre-selected */}
      {!isPreSelected && availableTripTypes.length > 0 && (
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Trip Type *
          </label>
          <div className="grid grid-cols-2 gap-3">
            {availableTripTypes.map((type) => (
              <label key={type} className="cursor-pointer">
                <input
                  type="radio"
                  className="sr-only peer"
                  name="tripType"
                  value={type}
                  checked={tripType === type}
                  onChange={() => {
                    setTripType(type);
                    setSelectedCoverage(null);
                    setDestRegion("");
                    setDestTown("");
                  }}
                />
                <div className="p-4 text-sm font-medium text-center rounded-xl border border-gray-200 transition-all peer-checked:border-emerald-400 peer-checked:ring-2 peer-checked:ring-emerald-100 peer-checked:bg-emerald-50 hover:border-emerald-200">
                  {type === "regional"
                    ? "Regional Travel"
                    : "International Travel"}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Destination Selection - Only show if not pre-selected */}
      {!isPreSelected && tripType && availableDestinations.length > 0 && (
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Select Destination *
          </label>
          <div className="grid overflow-y-auto grid-cols-1 gap-3 p-2 max-h-48 bg-gray-50 rounded-xl">
            {availableDestinations.map((dest, index) => (
              <label key={index} className="cursor-pointer">
                <input
                  type="radio"
                  className="sr-only peer"
                  name="destination"
                  checked={selectedCoverage?.region === dest.region}
                  onChange={() => setSelectedCoverage(dest)}
                />
                <div className="p-4 bg-white rounded-xl border border-gray-200 transition-all peer-checked:border-emerald-400 peer-checked:ring-2 peer-checked:ring-emerald-100 peer-checked:bg-white hover:border-emerald-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {dest.region}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        {dest.hours} {dest.unit} coverage
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-600">
                        ₵{dest.price}
                      </p>
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Bus License Plate */}
      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          Bus License Plate Number *
        </label>
        <input
          type="text"
          placeholder="e.g., GR-1234-21"
          value={busLicensePlate}
          onChange={(e) => setBusLicensePlate(e.target.value.toUpperCase())}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
      </div>

      {/* Vehicle Type */}
      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          Vehicle Type *
        </label>
        <div className="grid grid-cols-2 gap-2">
          {VEHICLE_TYPES.map((type) => (
            <label key={type} className="cursor-pointer">
              <input
                type="radio"
                className="sr-only peer"
                name="vehicleType"
                value={type}
                checked={vehicleType === type}
                onChange={() => setVehicleType(type)}
              />
              <div className="p-3 text-sm font-medium text-center rounded-xl border border-gray-200 peer-checked:border-emerald-400 peer-checked:ring-2 peer-checked:ring-emerald-100 hover:border-emerald-200">
                {type}
              </div>
            </label>
          ))}
        </div>

        <AnimatePresence>
          {vehicleType === "Other" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3"
            >
              <input
                type="text"
                placeholder="Specify vehicle type"
                value={customVehicleType}
                onChange={(e) => setCustomVehicleType(e.target.value)}
                className="px-4 py-3 w-full rounded-xl border border-gray-300 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Departure Details */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Departure Region *
          </label>
          <div className="px-4 py-3 w-full font-medium text-gray-700 bg-emerald-50 rounded-xl border border-emerald-300 flex items-center gap-2">
            <svg
              className="w-4 h-4 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {depRegion}
          </div>
        </div>

        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Departure City/Town *
          </label>
          <SearchableDropdown
            options={departureCities}
            value={depTown}
            onChange={(value) => setDepTown(value)}
            placeholder="Select City/Town"
            showOther={true}
            otherPlaceholder="Enter custom city/town"
            maxHeight="150px"
          />
        </div>
      </div>

      {/* Destination Details */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Destination Region *
          </label>
          <div className="px-4 py-3 w-full font-medium text-gray-700 bg-emerald-50 rounded-xl border border-emerald-300 flex items-center gap-2">
            <svg
              className="w-4 h-4 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {destRegion}
          </div>
        </div>

        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-800">
            Destination City/Town *
          </label>
          <SearchableDropdown
            options={destinationCities}
            value={destTown}
            onChange={(value) => setDestTown(value)}
            placeholder="Select City/Town"
            showOther={true}
            otherPlaceholder="Enter custom city/town"
            maxHeight="150px"
            disabled={!destRegion}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid}
          className="flex-1 px-4 py-3 font-semibold text-white bg-emerald-600 rounded-xl transition-colors hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
}
